import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useApiQuery } from './useApi.ts';
import { useAuthContext } from './contexts.tsx';
import { AUTH_GATEWAY_ENDPOINTS } from './authGatewayContract.ts';

/**
 * Effective permissions + roles for the current user, resolved by the gateway
 * from the database (TyndoAuthorizationService.GetUserPermissionsAsync) rather
 * than decoded from the JWT.
 *
 * The gateway validates Keycloak tokens with RoleClaimType='role' and does NOT
 * inject a flat `permissions` array into the token, so the previous approach of
 * reading `decoded.permissions` returned empty in production — every gated
 * control hid. This context fetches the authoritative set instead.
 *
 * CONVENTION (cross-lane): backed by the gateway endpoint
 *   GET /api/v1/auth/me
 * returning `{ userId: string; permissions: string[]; roles: string[] }` where
 * each permission is a `"<resource>:<action>"` string (wildcards
 * `"<resource>:*"` / `"*:*"` supported). The gateway side is owned by the
 * Tyndo.Gateway lane; the path is sourced from {@link AUTH_GATEWAY_ENDPOINTS}
 * so a frontend/gateway drift is caught by the auth contract test rather than
 * 404-ing silently (which would empty every permission set in production).
 */
export interface EffectivePermissions {
  /** Flat `"resource:action"` permission strings (supports wildcards). */
  permissions: string[];
  /** Role names assigned to the user. */
  roles: string[];
  /**
   * The principal's IdP-asserted preferred locale (OIDC `locale` claim), or
   * absent when the token carries none. The shell folds this into its locale
   * resolution; it is NOT the applied locale (the shell clamps to the locales
   * that actually ship a bundle before applying).
   */
  locale?: string | null;
}

export interface PermissionsContextValue {
  permissions: string[];
  roles: string[];
  loading: boolean;
  error?: Error;
  /** True when the wildcard dev-token fallback is in effect (no real fetch). */
  isWildcard: boolean;
  /**
   * The user's IdP-asserted preferred locale, forwarded from `/auth/me`. Null
   * for dev-token / signed-out sessions or when the token carries no `locale`
   * claim. The shell's locale bootstrap reads this as the highest-priority
   * candidate (user > tenant default > browser).
   */
  locale?: string | null;
}

export const PermissionsContext = createContext<PermissionsContextValue | undefined>(
  undefined,
);

const EFFECTIVE_PERMISSIONS_PATH = AUTH_GATEWAY_ENDPOINTS.me.path;

/**
 * Provides the current user's effective permissions + roles, fetched from the
 * gateway. Wrap the authenticated app shell in this provider; `usePermission`
 * and `useCurrentUser` consume it.
 *
 * Dev-token escape hatch: when the auth token is a `dev-token-*` value (the
 * gateway's local bypass used by Playwright auth setup before Keycloak is
 * configured), we short-circuit to wildcard permissions instead of hitting the
 * endpoint — matching the gateway's dev bypass so local flows aren't gated.
 */
export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { token } = useAuthContext();
  const isDevToken = Boolean(token?.startsWith('dev-token-'));
  const isAuthenticated = Boolean(token);

  const query = useApiQuery<EffectivePermissions>(EFFECTIVE_PERMISSIONS_PATH, {
    // Skip the fetch for the dev-token (wildcard fallback) and when signed out.
    enabled: isAuthenticated && !isDevToken,
    staleTime: 5 * 60 * 1000,
  });

  const value = useMemo<PermissionsContextValue>(() => {
    if (isDevToken) {
      return {
        permissions: ['*:*'],
        roles: ['*'],
        loading: false,
        isWildcard: true,
        locale: null,
      };
    }
    if (!isAuthenticated) {
      return { permissions: [], roles: [], loading: false, isWildcard: false, locale: null };
    }
    return {
      permissions: query.data?.permissions ?? [],
      roles: query.data?.roles ?? [],
      loading: query.isLoading,
      error: query.error
        ? new Error(query.error.message ?? 'Failed to load permissions')
        : undefined,
      isWildcard: false,
      locale: query.data?.locale ?? null,
    };
  }, [isDevToken, isAuthenticated, query.data, query.isLoading, query.error]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Read the effective-permissions context. When no PermissionsProvider is
 * mounted (e.g. unit tests or trees that haven't adopted it yet), returns an
 * empty, non-loading set so callers degrade to "no permission" rather than
 * crashing.
 */
export const usePermissionsContext = (): PermissionsContextValue => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    return { permissions: [], roles: [], loading: false, isWildcard: false, locale: null };
  }
  return ctx;
};
